import React, { useEffect, useMemo, useState } from 'react';
import { peopleForPov, styleCardsApi } from '../../api/styleCards';
import {
  bookPovSaveArgs,
  cameraShown,
  draftFromBookPov,
  helpText,
  POV_DEFERRED_VALUE,
  povNeedsSettle,
} from '../../utils/narrativePov';
import { POV_MODES } from '../../utils/styleCardsPath';
import {
  POV_CAMERA,
  POV_CAMERA_ONE,
  POV_CAMERA_SEVERAL,
  POV_CAST,
  POV_CAST_EMPTY,
  POV_CHIP_DEFERRED,
  POV_CHIP_UNSET,
  POV_DISTINCTION,
  POV_DONT_KNOW,
  POV_HELP_EXAMPLES,
  POV_SAVE,
  POV_STRIP_LEDE,
  POV_VIEWPOINT,
  PERSON,
} from '../../utils/writerCopy';
import './NarrativePovSettle.css';

function castName(id, people, bookPov) {
  const match = (people || []).find((person) => person.pickId === id);
  if (match?.name) return match.name;
  const saved = (bookPov?.cast || []).find((person) => person.entity_id === id);
  return saved?.name || 'Person';
}

export function NarrativePovForm({
  value,
  people = [],
  busy = false,
  onSave,
  onDirtyChange,
  idPrefix = 'npov',
}) {
  const signature = JSON.stringify({
    mode: value?.mode || '',
    deferred: Boolean(value?.deferred || value?.status === 'deferred'),
    camera: value?.camera || '',
    entity: value?.entity_id || '',
    cast: (value?.cast || []).map((person) => person.entity_id),
  });
  const [draft, setDraft] = useState(() => draftFromBookPov(value));
  const [seen, setSeen] = useState(signature);
  if (seen !== signature) {
    setSeen(signature);
    setDraft(draftFromBookPov(value));
  }

  const baseline = draftFromBookPov(value);
  const povDirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  useEffect(() => {
    onDirtyChange?.(povDirty);
  }, [povDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const deferred = draft.person === POV_DEFERRED_VALUE;
  const showCamera = cameraShown(draft.person, deferred);
  const showOne = showCamera && draft.camera === 'one';
  const showCast = showCamera && draft.camera === 'several';
  const help = helpText({ person: draft.person, camera: draft.camera, deferred });
  const viewpointPeople = useMemo(() => {
    const rows = [...(people || [])];
    if (draft.entityId && !rows.some((person) => person.pickId === draft.entityId) && value?.entity_name) {
      rows.push({ pickId: draft.entityId, name: value.entity_name });
    }
    return rows;
  }, [people, draft.entityId, value?.entity_name]);

  const setPerson = (person) => {
    setDraft((prev) => ({
      person,
      camera: cameraShown(person, person === POV_DEFERRED_VALUE) ? prev.camera : '',
      entityId: person === POV_DEFERRED_VALUE || person === 'omniscient' ? '' : prev.entityId,
      castIds: person === POV_DEFERRED_VALUE || person === 'omniscient' ? [] : prev.castIds,
    }));
  };

  const addCast = (pickId) => {
    if (!pickId) return;
    setDraft((prev) => (
      prev.castIds.includes(pickId) ? prev : { ...prev, castIds: [...prev.castIds, pickId] }
    ));
  };

  const moveCast = (index, delta) => {
    setDraft((prev) => {
      const next = [...prev.castIds];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return { ...prev, castIds: next };
    });
  };

  return (
    <form
      className="npov"
      data-testid="narrative-pov-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave?.(bookPovSaveArgs(draft, people));
      }}
    >
      <p className="npov__lede">{POV_STRIP_LEDE}</p>
      <p className="npov__hint">{POV_DISTINCTION}</p>
      <div className="npov__row">
        <label className="npov__field" htmlFor={`${idPrefix}-person`}>
          <span>{PERSON}</span>
          <select
            id={`${idPrefix}-person`}
            value={draft.person}
            disabled={busy}
            onChange={(event) => setPerson(event.target.value)}
          >
            <option value="">{POV_MODES[0].label}</option>
            <option value={POV_DEFERRED_VALUE}>{POV_DONT_KNOW}</option>
            {POV_MODES.filter((mode) => mode.value).map((mode) => (
              <option key={mode.value} value={mode.value}>{mode.label}</option>
            ))}
          </select>
        </label>
        {showCamera ? (
          <label className="npov__field" htmlFor={`${idPrefix}-camera`}>
            <span>{POV_CAMERA}</span>
            <select
              id={`${idPrefix}-camera`}
              value={draft.camera}
              disabled={busy}
              onChange={(event) => setDraft((prev) => ({
                ...prev,
                camera: event.target.value,
                entityId: event.target.value === 'several' ? '' : prev.entityId,
                castIds: event.target.value === 'one' ? [] : prev.castIds,
              }))}
            >
              <option value="">Not set</option>
              <option value="one">{POV_CAMERA_ONE}</option>
              <option value="several">{POV_CAMERA_SEVERAL}</option>
            </select>
          </label>
        ) : null}
        {showOne ? (
          <label className="npov__field" htmlFor={`${idPrefix}-viewpoint`}>
            <span>{POV_VIEWPOINT}</span>
            <select
              id={`${idPrefix}-viewpoint`}
              value={draft.entityId}
              disabled={busy}
              onChange={(event) => setDraft((prev) => ({ ...prev, entityId: event.target.value }))}
            >
              <option value="">Not set</option>
              {viewpointPeople.map((person) => (
                <option key={person.pickId} value={person.pickId}>{person.name}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <p className="npov__help" data-testid="narrative-pov-examples">{POV_HELP_EXAMPLES}</p>
      {help ? <p className="npov__help" data-testid="narrative-pov-help">{help}</p> : null}
      {showCast ? (
        <div data-testid="narrative-pov-cast">
          <p className="npov__hint">{POV_CAST}</p>
          {people.length === 0 && draft.castIds.length === 0 ? (
            <p className="npov__empty">{POV_CAST_EMPTY}</p>
          ) : null}
          <ul className="npov__cast">
            {draft.castIds.map((id, index) => (
              <li key={id} className="npov__cast-row">
                <span className="npov__cast-name">{castName(id, people, value)}</span>
                <button type="button" className="npov__btn" disabled={busy || index === 0} onClick={() => moveCast(index, -1)}>
                  {`Move ${castName(id, people, value)} earlier`}
                </button>
                <button
                  type="button"
                  className="npov__btn"
                  disabled={busy || index === draft.castIds.length - 1}
                  onClick={() => moveCast(index, 1)}
                >
                  {`Move ${castName(id, people, value)} later`}
                </button>
                <button
                  type="button"
                  className="npov__btn"
                  disabled={busy}
                  onClick={() => setDraft((prev) => ({
                    ...prev,
                    castIds: prev.castIds.filter((item) => item !== id),
                  }))}
                >
                  {`Remove ${castName(id, people, value)}`}
                </button>
              </li>
            ))}
          </ul>
          <label className="npov__field" htmlFor={`${idPrefix}-add-cast`}>
            <span>Add as a POV character</span>
            <select
              id={`${idPrefix}-add-cast`}
              value=""
              disabled={busy || people.length === 0}
              onChange={(event) => addCast(event.target.value)}
            >
              <option value="">Add a person</option>
              {people.filter((person) => !draft.castIds.includes(person.pickId)).map((person) => (
                <option key={person.pickId} value={person.pickId}>{person.name}</option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      <div className="npov__actions">
        <button type="submit" className="npov__btn npov__btn--primary" disabled={busy}>
          {POV_SAVE}
        </button>
      </div>
    </form>
  );
}

export default function NarrativePovStrip({
  storyId,
  people,
  onChange,
  onSaved,
}) {
  const [bookPov, setBookPov] = useState(null);
  const [loadedPeople, setLoadedPeople] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const roster = people || loadedPeople || [];

  useEffect(() => {
    if (!storyId) {
      setBookPov(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await styleCardsApi.list(storyId);
        if (cancelled) return;
        const next = list?.book_pov || { mode: null, deferred: false, status: 'unset' };
        setBookPov(next);
        onChange?.(next);
      } catch {
        if (!cancelled) setBookPov(null);
      }
      if (people) return;
      try {
        const outline = await styleCardsApi.outline(storyId);
        if (!cancelled) setLoadedPeople(peopleForPov(outline));
      } catch {
        if (!cancelled) setLoadedPeople([]);
      }
    })();
    return () => { cancelled = true; };
  }, [storyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!povNeedsSettle(bookPov)) return null;
  const chip = bookPov?.deferred || bookPov?.status === 'deferred'
    ? POV_CHIP_DEFERRED
    : POV_CHIP_UNSET;

  return (
    <section className="npov npov--strip" aria-label="Narrative POV" data-testid="narrative-pov-strip">
      {!open ? <p className="npov__lede">{POV_STRIP_LEDE}</p> : null}
      <button
        type="button"
        className="npov__chip"
        aria-expanded={open}
        data-testid="narrative-pov-chip"
        onClick={() => setOpen((prev) => !prev)}
      >
        {chip}
      </button>
      {open ? (
        <NarrativePovForm
          value={bookPov}
          people={roster}
          busy={busy}
          idPrefix="npov-strip"
          onSave={async (args) => {
            if (!storyId) return;
            setBusy(true);
            try {
              const saved = await styleCardsApi.setBookPov(storyId, args);
              const next = saved || bookPov;
              setBookPov(next);
              onChange?.(next);
              onSaved?.(next);
              if (!povNeedsSettle(next)) setOpen(false);
            } catch {
              setOpen(true);
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </section>
  );
}
